// frontend/src/features/tasks/TaskNotificationIcon.tsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ActionIcon,
  Indicator,
  Popover,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  ScrollArea,
  Divider,
  Box,
} from '@mantine/core';
import { IconChecklist, IconCheck } from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import { useTaskInstances, useCompleteTask } from '@/hooks/useTasks';
import type { TaskInstance } from '@/types/task';

function formatDueTime(dueAt: string): string {
  const due = new Date(dueAt);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  const diffDays = Math.floor(
    (dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) {
    return `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} overdue`;
  } else if (diffDays === 0) {
    return due.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else {
    return due.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
}

interface TaskItemProps {
  instance: TaskInstance;
  onComplete: (instance: TaskInstance) => void;
}

function TaskItem({ instance, onComplete }: TaskItemProps) {
  const dueTime = formatDueTime(instance.due_at);
  const isOverdue = new Date(instance.due_at) < new Date();

  return (
    <Group justify="space-between" wrap="nowrap" gap="xs">
      <Box style={{ minWidth: 0, flex: 1 }}>
        <Text size="sm" fw={500} truncate>
          {instance.name}
        </Text>
        <Text size="xs" c={isOverdue ? 'red' : 'dimmed'}>
          {dueTime}
        </Text>
      </Box>
      <ActionIcon
        variant="subtle"
        color="green"
        size="sm"
        onClick={() => onComplete(instance)}
        aria-label="Complete task"
      >
        <IconCheck size={14} />
      </ActionIcon>
    </Group>
  );
}

export function TaskNotificationIcon() {
  const { selectedTank } = useTank();
  const { data } = useTaskInstances();
  const completeTask = useCompleteTask();
  const [opened, setOpened] = useState(false);

  // Return null if no tank selected
  if (!selectedTank) {
    return null;
  }

  const instances = data?.instances ?? [];
  const overdueCount = data?.overdue_count ?? 0;
  const dueTodayCount = data?.due_today_count ?? 0;
  const urgentCount = overdueCount + dueTodayCount;

  // Filter to only urgent tasks (overdue or due today)
  const urgentTasks = instances.filter((instance) => {
    const dueDate = new Date(instance.due_at);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return dueDate <= today;
  });

  const handleComplete = (instance: TaskInstance) => {
    completeTask.mutate({
      instanceId: instance.id,
      data: {},
    });
  };

  // Determine indicator color: red for overdue, orange for due today only
  const indicatorColor = overdueCount > 0 ? 'red' : 'orange';

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={300}
      shadow="md"
    >
      <Popover.Target>
        <Indicator
          disabled={urgentCount === 0}
          label={urgentCount > 0 ? urgentCount : undefined}
          color={indicatorColor}
          size={16}
          offset={4}
          processing={urgentCount > 0}
        >
          <ActionIcon
            variant="subtle"
            size="lg"
            title="Tasks"
            aria-label="Tasks"
            onClick={() => setOpened((o) => !o)}
          >
            <IconChecklist size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>Due Tasks</Text>
            {overdueCount > 0 && (
              <Badge color="red" size="sm">
                {overdueCount} overdue
              </Badge>
            )}
          </Group>

          <Divider />

          {urgentTasks.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No tasks due
            </Text>
          ) : (
            <ScrollArea.Autosize mah={200}>
              <Stack gap="xs">
                {urgentTasks.map((instance) => (
                  <TaskItem
                    key={instance.id}
                    instance={instance}
                    onComplete={handleComplete}
                  />
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}

          <Divider />

          <Button
            component={Link}
            to="/tasks"
            variant="subtle"
            size="xs"
            fullWidth
            onClick={() => setOpened(false)}
          >
            View All Tasks
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
